import { useState } from 'react';
import {
  Award,
  Plus,
  Calendar,
  Building2,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Upload,
  ExternalLink,
} from 'lucide-react';
import {
  Card, CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  type Certification, CertificationStatus 
} from '@/types/certifications';

const mockCertifications: Certification[] = [
  {
    id: 'cert-1',
    name: 'HACCP Certification',
    issuingBody: 'NSF International',
    certificateNumber: 'HACCP-2024-78234',
    issueDate: '2024-01-15',
    expiryDate: '2027-01-15',
    status: CertificationStatus.ACTIVE,
    documentUrl: '/documents/haccp-cert.pdf',
    categories: ['Food Safety', 'Quality Management'],
  },
  {
    id: 'cert-2',
    name: 'FDA Registered Facility',
    issuingBody: 'U.S. Food and Drug Administration',
    certificateNumber: 'FDA-REG-12847563',
    issueDate: '2023-06-01',
    expiryDate: '2025-06-01',
    status: CertificationStatus.ACTIVE,
    documentUrl: '/documents/fda-registration.pdf',
    categories: ['Regulatory Compliance'],
  },
  {
    id: 'cert-3',
    name: 'USDA Organic Certification',
    issuingBody: 'Oregon Tilth',
    certificateNumber: 'OT-ORG-2024-4521',
    issueDate: '2024-03-01',
    expiryDate: '2025-03-01',
    status: CertificationStatus.EXPIRING_SOON,
    documentUrl: '/documents/organic-cert.pdf',
    categories: ['Organic', 'Sustainability'],
  },
  {
    id: 'cert-4',
    name: 'SQF Level 3 Certification',
    issuingBody: 'Safe Quality Food Institute',
    certificateNumber: 'SQF-L3-2023-9182',
    issueDate: '2023-09-15',
    expiryDate: '2024-09-15',
    status: CertificationStatus.EXPIRED,
    categories: ['Food Safety', 'Quality Management'],
  },
  {
    id: 'cert-5',
    name: 'Halal Certification',
    issuingBody: 'Islamic Food and Nutrition Council of America',
    certificateNumber: 'IFANCA-2025-3847',
    issueDate: '2025-01-10',
    expiryDate: '2026-01-10',
    status: CertificationStatus.PENDING,
    categories: ['Dietary', 'Religious Compliance'],
  },
];

function getStatusConfig(status: CertificationStatus) {
  switch (status) {
    case CertificationStatus.ACTIVE:
      return {
        label: 'Active',
        icon: CheckCircle2,
        className: 'bg-green-50 text-green-700 border-green-200',
        iconClassName: 'text-green-600',
      };
    case CertificationStatus.EXPIRING_SOON:
      return {
        label: 'Expiring Soon',
        icon: AlertTriangle,
        className: 'bg-amber-50 text-amber-700 border-amber-200',
        iconClassName: 'text-amber-600',
      };
    case CertificationStatus.EXPIRED:
      return {
        label: 'Expired',
        icon: XCircle,
        className: 'bg-red-50 text-red-700 border-red-200',
        iconClassName: 'text-red-600',
      };
    case CertificationStatus.PENDING:
      return {
        label: 'Pending Review',
        icon: Clock,
        className: 'bg-blue-50 text-blue-700 border-blue-200',
        iconClassName: 'text-blue-600',
      };
  }
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getDaysUntilExpiry(expiryDate: string): number {
  const expiry = new Date(expiryDate);
  const today = new Date();
  const diffTime = expiry.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function getExpiryTextColor(status: CertificationStatus): string {
  switch (status) {
    case CertificationStatus.EXPIRED:
      return 'text-red-600';
    case CertificationStatus.EXPIRING_SOON:
      return 'text-amber-600';
    default:
      return '';
  }
}

interface CertificationCardProps {readonly certification: Certification;}

function CertificationCard({ certification }: CertificationCardProps) {
  const statusConfig = getStatusConfig(certification.status);
  const StatusIcon = statusConfig.icon;
  const daysUntilExpiry = getDaysUntilExpiry(certification.expiryDate);

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="rounded-lg bg-primary/10 p-2.5">
              <Award className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base">{certification.name}</h3>
              <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
                <Building2 className="h-3.5 w-3.5" />
                <span>{certification.issuingBody}</span>
              </div>
            </div>
          </div>
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${statusConfig.className}`}>
            <StatusIcon className={`h-3.5 w-3.5 ${statusConfig.iconClassName}`} />
            {statusConfig.label}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground text-xs">Certificate Number</p>
            <p className="font-medium mt-0.5">{certification.certificateNumber}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Issue Date</p>
            <p className="font-medium mt-0.5">{formatDate(certification.issueDate)}</p>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-sm">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">Expires:</span>
            <span className={`font-medium ${getExpiryTextColor(certification.status)}`}>
              {formatDate(certification.expiryDate)}
              {certification.status === CertificationStatus.EXPIRING_SOON && (
                <span className="ml-1">({daysUntilExpiry} days)</span>
              )}
            </span>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {certification.categories.map((category) => (
            <span
              key={category}
              className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            >
              {category}
            </span>
          ))}
        </div>

        <div className="mt-4 flex gap-2">
          {certification.documentUrl && (
            <Button variant="outline" size="sm" className="flex-1">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              View Document
            </Button>
          )}
          <Button variant="outline" size="sm" className="flex-1">
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            {certification.status === CertificationStatus.EXPIRED ? 'Renew' : 'Update'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function CertificationsPage() {
  const [filter, setFilter] = useState<CertificationStatus | 'all'>('all');

  const filteredCertifications = filter === 'all'
    ? mockCertifications
    : mockCertifications.filter((cert) => cert.status === filter);

  const statusCounts = {
    all: mockCertifications.length,
    active: mockCertifications.filter((c) => c.status === CertificationStatus.ACTIVE).length,
    expiring_soon: mockCertifications.filter((c) => c.status === CertificationStatus.EXPIRING_SOON).length,
    expired: mockCertifications.filter((c) => c.status === CertificationStatus.EXPIRED).length,
    pending: mockCertifications.filter((c) => c.status === CertificationStatus.PENDING).length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Certifications</h1>
          <p className="text-muted-foreground">Manage your company certifications and compliance documents</p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Certification
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => setFilter('all')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{statusCounts.all}</p>
              </div>
              <FileText className="h-8 w-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-green-500 transition-colors" onClick={() => setFilter(CertificationStatus.ACTIVE)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-green-600">{statusCounts.active}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-amber-500 transition-colors" onClick={() => setFilter(CertificationStatus.EXPIRING_SOON)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Expiring Soon</p>
                <p className="text-2xl font-bold text-amber-600">{statusCounts.expiring_soon}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-amber-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-red-500 transition-colors" onClick={() => setFilter(CertificationStatus.EXPIRED)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Expired</p>
                <p className="text-2xl font-bold text-red-600">{statusCounts.expired}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {statusCounts.expiring_soon > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">
                  {statusCounts.expiring_soon} certification{statusCounts.expiring_soon > 1 ? 's' : ''} expiring soon
                </p>
                <p className="text-sm text-amber-700">
                  Review and renew your certifications to maintain compliance
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Filter:</span>
        <div className="flex gap-1">
          {[
            {
              value: 'all',
              label: 'All' 
            },
            {
              value: CertificationStatus.ACTIVE,
              label: 'Active' 
            },
            {
              value: CertificationStatus.EXPIRING_SOON,
              label: 'Expiring' 
            },
            {
              value: CertificationStatus.EXPIRED,
              label: 'Expired' 
            },
            {
              value: CertificationStatus.PENDING,
              label: 'Pending' 
            },
          ].map((option) => (
            <Button
              key={option.value}
              variant={filter === option.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(option.value as CertificationStatus | 'all')}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredCertifications.map((certification) => (
          <CertificationCard key={certification.id} certification={certification} />
        ))}
      </div>

      {filteredCertifications.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Award className="h-12 w-12 mx-auto text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-medium">No certifications found</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {filter === 'all'
                ? 'Add your first certification to get started'
                : `No certifications with status "${filter}"`}
            </p>
            {filter === 'all' && (
              <Button className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add Certification
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
